/**
 * Collection metadata for MongoDB backup system
 * Provides descriptions and category groupings for all backed-up collections
 */

// Collection descriptions and categories
export const COLLECTION_META = {
  // Core Website Content
  admin_users: {
    category: 'Admin',
    description: 'Administrator accounts and permissions',
    icon: 'users',
  },
  api_settings: {
    category: 'Admin',
    description: 'API keys for external services (fal.ai, Google TTS, etc.)',
    icon: 'key',
  },
  business_info: {
    category: 'Website Content',
    description: 'Studio contact info, hours, and business details',
    icon: 'building',
  },
  belts: {
    category: 'Website Content',
    description: 'Belt rank configuration (colors, order, names)',
    icon: 'award',
  },
  hero_content: {
    category: 'Website Content',
    description: 'Homepage hero section (title, image, CTA)',
    icon: 'image',
  },
  lineage_sections: {
    category: 'Website Content',
    description: 'Martial arts lineage and history content',
    icon: 'book',
  },
  programs: {
    category: 'Website Content',
    description: 'Martial arts programs offered',
    icon: 'file-text',
  },
  schedules: {
    category: 'Website Content',
    description: 'Weekly class schedule',
    icon: 'calendar',
  },
  testimonials: {
    category: 'Website Content',
    description: 'Customer reviews and testimonials',
    icon: 'star',
  },
  gallery: {
    category: 'Website Content',
    description: 'Photo and video gallery items',
    icon: 'image',
  },
  navigation_links: {
    category: 'Website Content',
    description: 'Header and footer navigation links',
    icon: 'menu',
  },
  custom_pages: {
    category: 'Website Content',
    description: 'Custom pages created with page builder',
    icon: 'file-plus',
  },
  custom_page_versions: {
    category: 'Website Content',
    description: 'Version history for custom pages',
    icon: 'history',
  },

  // Homepage Sections
  expectation_steps: {
    category: 'Website Content',
    description: '"What to Expect" section steps',
    icon: 'list-checks',
  },
  expectation_cta: {
    category: 'Website Content',
    description: '"What to Expect" call-to-action button',
    icon: 'mouse-pointer',
  },
  safety_privacy: {
    category: 'Website Content',
    description: 'Safety and privacy policy content',
    icon: 'shield',
  },
  trust_indicators: {
    category: 'Website Content',
    description: 'Trust badges on hero section',
    icon: 'badge',
  },
  why_owaza: {
    category: 'Website Content',
    description: '"Why Choose Owaza" feature cards',
    icon: 'grid',
  },
  public_events: {
    category: 'Website Content',
    description: 'Public events, tournaments, and seminars',
    icon: 'calendar-days',
  },
  qr_code_configs: {
    category: 'Admin',
    description: 'QR code configurations for print materials',
    icon: 'qr-code',
  },

  // Learning System - Content
  vocabulary_terms: {
    category: 'Learning Content',
    description: 'Japanese vocabulary terms and definitions',
    icon: 'languages',
  },
  quizzes: {
    category: 'Learning Content',
    description: 'Quiz definitions and settings',
    icon: 'file-question',
  },
  questions: {
    category: 'Learning Content',
    description: 'Quiz questions (multiple choice, short answer)',
    icon: 'help-circle',
  },
  badges: {
    category: 'Learning Content',
    description: 'Achievement badge definitions',
    icon: 'medal',
  },
  techniques: {
    category: 'Learning Content',
    description: 'Martial arts technique definitions',
    icon: 'target',
  },
  vocab_points_config: {
    category: 'Learning Content',
    description: 'Point values for vocabulary activities',
    icon: 'settings',
  },
  leaderboard_config: {
    category: 'Learning Content',
    description: 'Leaderboard display settings',
    icon: 'trophy',
  },

  // Learning System - Progress
  vocabulary_attempts: {
    category: 'Student Progress',
    description: 'Student vocabulary practice attempts',
    icon: 'check-circle',
  },
  quiz_attempts: {
    category: 'Student Progress',
    description: 'Student quiz attempt history',
    icon: 'clipboard-check',
  },
  unlocked_badges: {
    category: 'Student Progress',
    description: 'Badges earned by students',
    icon: 'award',
  },
  student_techniques: {
    category: 'Student Progress',
    description: 'Techniques learned by students',
    icon: 'target',
  },
  admin_review_queue: {
    category: 'Student Progress',
    description: 'Short answers pending admin review',
    icon: 'clipboard-list',
  },
  student_points: {
    category: 'Student Progress',
    description: 'Student point totals and stats',
    icon: 'trending-up',
  },
  points_transactions: {
    category: 'Student Progress',
    description: 'Point earning/spending history',
    icon: 'receipt',
  },
  student_vocabulary_progress: {
    category: 'Student Progress',
    description: 'Per-word mastery progress',
    icon: 'bar-chart',
  },
  category_mastery_progress: {
    category: 'Student Progress',
    description: 'Category-level mastery progress',
    icon: 'pie-chart',
  },
  global_achievements: {
    category: 'Student Progress',
    description: 'Global achievement milestones',
    icon: 'star',
  },

  // Scenarios
  scenarios: {
    category: 'Scenarios',
    description: 'Branching scenario definitions',
    icon: 'git-branch',
  },
  scenario_nodes: {
    category: 'Scenarios',
    description: 'Individual scenario decision nodes',
    icon: 'git-commit',
  },
  scenario_attempts: {
    category: 'Scenarios',
    description: 'Student scenario playthrough history',
    icon: 'play-circle',
  },
  image_generation_jobs: {
    category: 'Scenarios',
    description: 'AI image generation job queue',
    icon: 'image-plus',
  },

  // Students & Customers
  students: {
    category: 'Students',
    description: 'Student profiles and belt ranks',
    icon: 'user',
  },
  customers: {
    category: 'Customers',
    description: 'Customer accounts (parents/guardians)',
    icon: 'users',
  },
  belt_promotions: {
    category: 'Students',
    description: 'Belt promotion history',
    icon: 'arrow-up-circle',
  },
  attendance: {
    category: 'Students',
    description: 'Class attendance records',
    icon: 'calendar-check',
  },
  tournaments: {
    category: 'Students',
    description: 'Tournament participation and results',
    icon: 'trophy',
  },
  resources: {
    category: 'Students',
    description: 'Training resources (videos, PDFs)',
    icon: 'folder',
  },
  subscription_settings: {
    category: 'Customers',
    description: 'Subscription plan settings',
    icon: 'credit-card',
  },
  payments: {
    category: 'Customers',
    description: 'Payment transaction history',
    icon: 'dollar-sign',
  },

  // Authentication
  magic_links: {
    category: 'Authentication',
    description: 'Pending magic link tokens (temporary)',
    icon: 'link',
  },
  otp_codes: {
    category: 'Authentication',
    description: 'One-time password codes (temporary)',
    icon: 'key',
  },
  impersonation_tokens: {
    category: 'Authentication',
    description: 'Admin impersonation tokens',
    icon: 'user-check',
  },
  impersonation_audit: {
    category: 'Authentication',
    description: 'Impersonation audit log',
    icon: 'eye',
  },
  admin_sessions: {
    category: 'Authentication',
    description: 'Active admin sessions',
    icon: 'log-in',
  },

  // Newsletter
  newsletters: {
    category: 'Newsletter',
    description: 'Newsletter drafts and sent emails',
    icon: 'mail',
  },
  newsletter_lists: {
    category: 'Newsletter',
    description: 'Mailing list definitions',
    icon: 'list',
  },
  newsletter_recipients: {
    category: 'Newsletter',
    description: 'Newsletter subscriber emails',
    icon: 'users',
  },
  newsletter_segments: {
    category: 'Newsletter',
    description: 'Subscriber segment rules',
    icon: 'filter',
  },
  newsletter_automations: {
    category: 'Newsletter',
    description: 'Email automation workflows',
    icon: 'zap',
  },
  newsletter_clicks: {
    category: 'Newsletter',
    description: 'Link click tracking data',
    icon: 'mouse-pointer',
  },
  newsletter_opens: {
    category: 'Newsletter',
    description: 'Email open tracking data',
    icon: 'eye',
  },
  newsletter_send_jobs: {
    category: 'Newsletter',
    description: 'Email send job queue',
    icon: 'send',
  },
  automation_runs: {
    category: 'Newsletter',
    description: 'Automation execution history',
    icon: 'activity',
  },

  // Admin Features
  feedback_submissions: {
    category: 'Admin',
    description: 'Customer feedback survey responses',
    icon: 'message-square',
  },
  saved_for_later: {
    category: 'Admin',
    description: 'Google reviews saved for later import',
    icon: 'bookmark',
  },
  release_notes: {
    category: 'Admin',
    description: 'Weekly release notes history',
    icon: 'file-text',
  },
};

// Category display order and colors
export const CATEGORY_CONFIG = {
  'Website Content': {
    order: 1,
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    description: 'Public website content visible to visitors',
  },
  'Learning Content': {
    order: 2,
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    description: 'Vocabulary, quizzes, and badge definitions',
  },
  'Student Progress': {
    order: 3,
    color: 'bg-green-100 text-green-800 border-green-200',
    description: 'Student learning progress and achievements',
  },
  'Scenarios': {
    order: 4,
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    description: 'Branching scenario training content',
  },
  'Students': {
    order: 5,
    color: 'bg-teal-100 text-teal-800 border-teal-200',
    description: 'Student profiles, attendance, and belt history',
  },
  'Customers': {
    order: 6,
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    description: 'Customer accounts and payment info',
  },
  'Newsletter': {
    order: 7,
    color: 'bg-pink-100 text-pink-800 border-pink-200',
    description: 'Email newsletter content and subscribers',
  },
  'Authentication': {
    order: 8,
    color: 'bg-stone-100 text-stone-800 border-stone-200',
    description: 'Login tokens and sessions (mostly temporary)',
  },
  'Admin': {
    order: 9,
    color: 'bg-red-100 text-red-800 border-red-200',
    description: 'Admin settings, users, and API keys',
  },
};

/**
 * Get metadata for a collection
 */
export function getCollectionMeta(collectionName) {
  return COLLECTION_META[collectionName] || {
    category: 'Other',
    description: 'Database collection',
    icon: 'database',
  };
}

/**
 * Group collections by category
 */
export function groupCollectionsByCategory(collections) {
  const groups = {};

  collections.forEach((collection) => {
    const name = typeof collection === 'string' ? collection : collection.name;
    const meta = getCollectionMeta(name);
    const category = meta.category;

    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(collection);
  });

  // Sort categories by order
  const sortedCategories = Object.keys(groups).sort((a, b) => {
    const orderA = CATEGORY_CONFIG[a]?.order || 99;
    const orderB = CATEGORY_CONFIG[b]?.order || 99;
    return orderA - orderB;
  });

  return sortedCategories.map((category) => ({
    category,
    config: CATEGORY_CONFIG[category] || {
      order: 99,
      color: 'bg-stone-100 text-stone-800 border-stone-200',
      description: 'Other collections',
    },
    collections: groups[category],
  }));
}
